import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { WizardLayout } from '../components/WizardLayout';
import { Chip } from '../components/ui/Chip';
import { RangeSlider } from '../components/ui/RangeSlider';
import { useMarketplace } from '../contexts/MarketplaceContext';
import { actTypes, eventTypes, genres } from '../data/options';
import { baht } from '../utils/format';
import type { ActType, EventType } from '../types/marketplace';
const steps = [{
  title: 'Who’s organising?',
  subtitle: 'Performers see this on every event you post.'
}, {
  title: 'What do you book?',
  subtitle: 'We’ll suggest acts that suit your events.'
}, {
  title: 'Set your defaults',
  subtitle: 'You can change these any time, per event too.'
}];
const venues = ['Rooftop bar', 'Wedding venue', 'Hotel', 'Festival site', 'Private home', 'Club', 'Restaurant'];
const field = 'h-14 w-full rounded-sm border border-line bg-surface px-4 text-[17px] font-semibold placeholder:font-normal placeholder:text-ink-3 focus:border-ink focus:outline-none';
const toggle = <T,>(list: T[], item: T) => list.includes(item) ? list.filter(x => x !== item) : [...list, item];
export function OrganiserOnboarding() {
  const navigate = useNavigate();
  const {
    organiserPrefs,
    setOrganiserPrefs,
    setRole
  } = useMarketplace();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [org, setOrg] = useState('');
  const [person, setPerson] = useState('');
  const [venueTypes, setVenueTypes] = useState<string[]>(['Rooftop bar']);
  const [types, setTypes] = useState<EventType[]>(['Bar gig', 'Private party']);
  const [bookGenres, setBookGenres] = useState<string[]>(organiserPrefs.genres);
  const [acts, setActs] = useState<ActType[]>(organiserPrefs.actTypes);
  const [budget, setBudget] = useState(organiserPrefs.budgetMax);
  const [distance, setDistance] = useState(organiserPrefs.maxDistanceKm);
  const go = (d: number) => {
    setDir(d);
    setStep(s => s + d);
  };
  const finish = () => {
    setOrganiserPrefs({
      ...organiserPrefs,
      genres: bookGenres,
      actTypes: acts,
      budgetMax: budget,
      maxDistanceKm: distance
    });
    setRole('organiser');
    toast.success(`Welcome, ${person.trim() || org.trim() || 'organiser'}`, {
      description: 'Post your first event to start getting applicants.'
    });
    navigate('/o/events/new');
  };
  const canNext = [org.trim().length > 1, bookGenres.length > 0 && acts.length > 0, true][step];
  return <WizardLayout step={step} total={steps.length} direction={dir} title={steps[step].title} subtitle={steps[step].subtitle} onBack={() => step === 0 ? navigate('/start') : go(-1)} onNext={() => step === steps.length - 1 ? finish() : go(1)} nextLabel={step === steps.length - 1 ? 'Post your first event' : 'Continue'} canNext={canNext} data-id="element-1115" data-mp="3elfwmefpw8q1">
      {step === 0 && <div className="space-y-6" data-id="element-1116" data-mp="1u6w50ff2i7o1">
          <label className="block" data-id="element-1117" data-mp="1mls7z03wo2r7">
            <span className="text-[15px] font-bold" data-id="element-1118" data-mp="1mkqyh02jfobl">Organisation or venue</span>
            <input autoFocus value={org} onChange={e => setOrg(e.target.value)} placeholder="e.g. Halcyon Rooftop" className={`${field} mt-2`} data-id="element-1119" data-mp="1hn8lmylegoyb" />
          </label>
          <label className="block" data-id="element-1120" data-mp="1vkdxjf7soi1h">
            <span className="text-[15px] font-bold" data-id="element-1121" data-mp="1qmvrbwl5wgx6">Your name</span>
            <input value={person} onChange={e => setPerson(e.target.value)} placeholder="e.g. Somchai Rattanakul" className={`${field} mt-2`} data-id="element-1122" data-mp="1qlkmrujw7550" />
          </label>
          <div data-id="element-1123" data-mp="yxiviosl0rcq">
            <p className="mb-3 text-[15px] font-bold" data-id="element-1124" data-mp="yy2apxqul1n2">Where do your events happen?</p>
            <div className="flex flex-wrap gap-2" data-id="element-1125" data-mp="1cp726ck2anmb">
              {venues.map(v => <Chip key={v} selected={venueTypes.includes(v)} onClick={() => setVenueTypes(l => toggle(l, v))} data-id="element-1126" data-mp="17w4905j75g6n">
                  {v}
                </Chip>)}
            </div>
          </div>
        </div>}

      {step === 1 && <div className="space-y-7" data-id="element-1127" data-mp="sv7f2ioy4h9t">
          <div data-id="element-1128" data-mp="sw0gd454yekk">
            <p className="mb-3 text-[15px] font-bold" data-id="element-1129" data-mp="swij8jmazr1g">Event types</p>
            <div className="flex flex-wrap gap-2" data-id="element-1130" data-mp="xt8zp820sone">
              {eventTypes.map(t => <Chip key={t} selected={types.includes(t)} onClick={() => setTypes(l => toggle(l, t))} data-id="element-1131" data-mp="12svalaudlst1">
                  {t}
                </Chip>)}
            </div>
          </div>
          <div data-id="element-1132" data-mp="1bm8bp02lg0q7">
            <p className="mb-3 text-[15px] font-bold" data-id="element-1133" data-mp="1bkf67o3pvmjs">Genres</p>
            <div className="flex flex-wrap gap-2" data-id="element-1134" data-mp="1gk9tlhoa9u03">
              {genres.map(g => <Chip key={g} selected={bookGenres.includes(g)} onClick={() => setBookGenres(l => toggle(l, g))} data-id="element-1135" data-mp="1ljmrqyklunhz">
                  {g}
                </Chip>)}
            </div>
          </div>
          <div data-id="element-1136" data-mp="3nb99e9ilx61s">
            <p className="mb-3 text-[15px] font-bold" data-id="element-1137" data-mp="3nbrfdxv2mymw">Acts you book</p>
            <div className="flex flex-wrap gap-2" data-id="element-1138" data-mp="3sa2q0at6xd66">
              {actTypes.map(a => <Chip key={a} selected={acts.includes(a)} onClick={() => setActs(l => toggle(l, a))} data-id="element-1139" data-mp="3sbxaxp4gp5a9">
                  {a}
                </Chip>)}
            </div>
          </div>
        </div>}

      {step === 2 && <div className="space-y-8" data-id="element-1140" data-mp="20luuu8nsb2jn">
          <RangeSlider label="Typical budget per act" min={2000} max={100000} step={1000} value={budget} onChange={setBudget} format={v => `up to ${baht(v)}`} data-id="element-1141" data-mp="20mw90m03k55l" />
          <RangeSlider label="How far should acts travel from?" min={5} max={250} step={5} value={distance} onChange={setDistance} format={v => `${v} km`} data-id="element-1142" data-mp="2scsobexz6r66" />
          <p className="rounded-md bg-surface-2 p-4 text-[15px] leading-relaxed text-ink-2" data-id="element-1143" data-mp="2jd5lcoplldb0">
            Posting is free. We only charge a 6% booking fee when a performer accepts your offer — and the payment is protected until after the gig.
          </p>
        </div>}
    </WizardLayout>;
}